'use client';

import { Behance, Chevron, DeviantArt, Facebook, Instagram, Link, Pinterest, Tumblr, Twitch, Twitter, Verified, Vimeo, Youtube } from '@sprd/sprd-component-kit/v2';
import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useDebounce } from '@/hooks/useDebounce';
import type { ShowroomData, SocialLinkType } from '@/app/actions/user';
import { useImageBaseUrl } from '@/atoms/processEnvAtom';
import { getUserImageSrc } from '@/utility/images';
const MAX_DESCRIPTION_LINES = 2;
type ShowroomUserInformationProps = {
  showroomData: ShowroomData;
};
export const ShowroomUserInformation = ({
  showroomData
}: ShowroomUserInformationProps) => {
  const t = useTranslations('mpShowroomPage');
  const imageBaseUrl = useImageBaseUrl();
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const [descriptionLines, setDescriptionLines] = useState(countLines(descriptionRef.current ?? undefined));
  const [showMore, setShowMore] = useState<boolean>(false);
  const linesToShow = showMore ? descriptionLines.lines ?? MAX_DESCRIPTION_LINES : MAX_DESCRIPTION_LINES;
  const isLongDescription = descriptionLines.lines > MAX_DESCRIPTION_LINES;
  const toggleShowMore = useCallback(() => setShowMore(prev => !prev), []);
  const recalculateEllipsis = useDebounce(() => {
    if (!descriptionRef.current) {
      return;
    }
    setDescriptionLines(countLines(descriptionRef.current));
  }, 100);
  useEffect(() => {
    recalculateEllipsis();
    addEventListener('resize', recalculateEllipsis);
    return () => removeEventListener('resize', recalculateEllipsis);
  }, [recalculateEllipsis]);
  const {
    id: userId,
    name: userName,
    partnerType
  } = showroomData;
  const {
    description,
    userLinks
  } = showroomData.designerSettings;
  return <div className="flex gap-6">
      <div className="relative flex-shrink-0">
        <img className="size-16 bg-white md:size-18" draggable={false} src={getUserImageSrc(imageBaseUrl, userId)} loading="eager" alt={t('a11y.userImageLabel', {
        userName
      })} />
        {partnerType === 'LICENSOR' && <span className="absolute -top-1.5 -right-1.5 md:-top-3 md:-right-3" title={t('a11y.officialMerch')}>
            <Verified className="size-5 md:size-8" aria-hidden />
          </span>}
      </div>
      {(!!description?.length || !!userLinks?.length) && <div className="flex-grow-1">
          <button className="mt-6 mb-2 inline-flex cursor-pointer items-center gap-0.5 truncate text-sm font-medium md:hidden" onClick={toggleShowMore} aria-expanded={showMore}>
            {t('moreAbout', {
          userName
        })}
            <Chevron className={clsx('size-6 shrink-0 duration-200 motion-safe:transition-transform', showMore && 'rotate-180')} />
          </button>
          <div className={clsx('flex flex-col gap-4 overflow-hidden duration-150 motion-safe:transition-all md:h-auto md:flex-row', showMore ? 'h-full' : 'h-0')}>
            {!!description?.length && <div className="md:max-w-2/3 lg:max-w-1/2">
                <div style={{
            maxHeight: `${descriptionLines.lineHeight * linesToShow}px`
          }} className={clsx('overflow-hidden duration-150 motion-safe:transition-all', showMore ? 'line-clamp-none' : 'line-clamp-2')}>
                  <p ref={descriptionRef} className="text-sm leading-normal md:text-base" dangerouslySetInnerHTML={{
              __html: description
            }} />
                </div>
                <button className={clsx('mt-0.5 hidden cursor-pointer items-center gap-0.5 text-sm font-medium md:inline-flex', isLongDescription ? 'opacity-100' : 'pointer-events-none opacity-0')} tabIndex={isLongDescription ? undefined : -1} onClick={toggleShowMore} aria-expanded={showMore}>
                  {t(showMore ? 'showLess' : 'showMore')}
                  <Chevron className={clsx('size-6 shrink-0 duration-200 motion-safe:transition-transform', showMore && 'rotate-180')} />
                </button>
              </div>}
            {!!userLinks?.length && <ul className="flex flex-grow-1 flex-row gap-3 md:justify-end md:gap-0 md:pt-4">
                {userLinks.map(({
            type,
            value
          }) => {
            const link = SOCIAL_LINKS[type];
            if (!link) {
              return null;
            }
            const Icon = link.icon;
            const href = link.basePath.replace('{id}', value);
            const title = type === 'CUSTOM' ? userName : link.channelName;
            return <li key={type}>
                      <a href={href} target="_blank" title={title} className="block md:p-2">
                        <Icon aria-hidden className="size-6" />
                      </a>
                    </li>;
          })}
              </ul>}
          </div>
        </div>}
    </div>;
};
const getLineHeight = (element: HTMLElement) => {
  if (typeof window === 'undefined') {
    return 0;
  }
  return parseInt(window.getComputedStyle(element).lineHeight);
};
const countLines = (element?: HTMLElement) => {
  if (!element) {
    return {
      elementHeight: MAX_DESCRIPTION_LINES * 24,
      lineHeight: 24,
      lines: MAX_DESCRIPTION_LINES
    };
  }
  const elementHeight = element.scrollHeight;
  const lineHeight = getLineHeight(element);
  return {
    elementHeight: elementHeight,
    lineHeight: lineHeight,
    lines: Math.floor(elementHeight / lineHeight)
  };
};
type IconComponent = typeof Behance;
type SocialLink = {
  channelName: string;
  basePath: string;
  icon: IconComponent;
};
const SOCIAL_LINKS: Record<SocialLinkType, SocialLink> = {
  BEHANCE: {
    channelName: 'Behance',
    basePath: 'https://www.behance.net/{id}',
    icon: Behance
  },
  CUSTOM: {
    channelName: 'Custom',
    basePath: '{id}',
    icon: Link
  },
  DEVIANTART: {
    channelName: 'DeviantArt',
    basePath: 'https://{id}.deviantart.com/',
    icon: DeviantArt
  },
  FACEBOOK: {
    channelName: 'Facebook',
    basePath: 'https://www.facebook.com/{id}',
    icon: Facebook
  },
  INSTAGRAM: {
    channelName: 'Instagram',
    basePath: 'https://www.instagram.com/{id}',
    icon: Instagram
  },
  PINTEREST: {
    channelName: 'Pinterest',
    basePath: 'https://www.pinterest.com/{id}',
    icon: Pinterest
  },
  TUMBLR: {
    channelName: 'Tumblr',
    basePath: 'https://{id}.tumblr.com/',
    icon: Tumblr
  },
  TWITCH: {
    channelName: 'Twitch',
    basePath: 'https://www.twitch.tv/{id}',
    icon: Twitch
  },
  TWITTER: {
    channelName: 'X',
    basePath: 'https://x.com/{id}',
    icon: Twitter
  },
  VIMEO: {
    channelName: 'Vimeo',
    basePath: 'https://vimeo.com/{id}',
    icon: Vimeo
  },
  YOUTUBE: {
    channelName: 'YouTube',
    basePath: 'https://www.youtube.com/@{id}',
    icon: Youtube
  }
};